"use client";

import { useEffect, useState } from "react";
import { ChartSpline, ChevronRight } from "lucide-react";
import {
  canAnchorOddsHistory,
  type OddsHistoryPeerBook,
  type OddsHistoryQuoteCapacity,
  type OddsHistorySelection,
  type OpenOddsHistory,
} from "@/components/odds/odds-history-selection";
import { canOpenBookHistory, dropEventLineHistory, findBookSeries, useEventLineHistory } from "@/components/odds/line-sparkline";
import { toBetLinkSet } from "@/lib/bet-links";
import type { EventHeader, MarketSpec } from "@/lib/odds-screen/live/types";
import { getBookDisplayName } from "@/lib/utils/sports-books";
import { decimalToAmerican } from "@/utils/calculators/odds-utils";
import { phCapture } from "@/utils/analytics";
import type { DrillTarget, RailBookRow, RailModel } from "./drill";
import { betHref } from "./bet";
import type { BookStake } from "./drill-rail";
import { matchupLabel } from "./labels";

export type LineHistoryRowProps = {
  target: DrillTarget;
  spec: MarketSpec;
  event: EventHeader | undefined;
  entityName: string | null;
  model: RailModel;
  modelAt: (line: number) => RailModel | null;
  bookStake: BookStake;
  isMobile: boolean;
  onOpenHistory: OpenOddsHistory;
  openFor?: { book: string; onOpened: () => void };
};

function selectionKey(row: RailBookRow): string | null {
  return row.selectionId !== null && row.side !== null ? `${row.selectionId}|${row.side.toLowerCase()}` : null;
}

function capacityOf(row: RailBookRow, bookStake: BookStake): OddsHistoryQuoteCapacity | undefined {
  const kind = bookStake.get(row.book);
  return row.stake !== null && row.stake > 0 && kind ? { kind: kind === "limit" ? "limit" : "liquidity", amount: row.stake } : undefined;
}

export function LineHistoryRow(props: LineHistoryRowProps) {
  const [attempt, setAttempt] = useState(0);
  return <LineHistoryRowData key={`${attempt}|${props.target.event}|${props.target.family}|${props.target.entity}|${props.target.slot}|${props.target.outcome}|${props.target.line}`} {...props} onRetry={() => { dropEventLineHistory(props.target.event); setAttempt((n) => n + 1); }} />;
}

function LineHistoryRowData({ target, spec, event, entityName, model, modelAt, bookStake, isMobile, onOpenHistory, openFor, onRetry }: LineHistoryRowProps & { onRetry: () => void }) {
  const rows = model.columns.find((column) => column.outcome === target.outcome)?.rows ?? [];
  const keys = rows.flatMap((row) => { const key = selectionKey(row); return key === null ? [] : [key]; });
  const eventStart = event === undefined ? null : new Date(event.startMs);
  const series = useEventLineHistory(target.event, keys.length > 0, eventStart, keys);
  const anchors = rows.flatMap((row) => {
    if (row.selectionId === null || row.side === null) return [];
    const found = findBookSeries(series, { book: row.book, selectionId: row.selectionId, side: row.side });
    return canOpenBookHistory(found, { book: row.book, selectionId: row.selectionId, side: row.side }) ? [{ row, side: row.side, series: found }] : [];
  });
  const anchor = anchors.find((candidate) => candidate.row.bettable) ?? anchors[0] ?? null;
  const loading = anchor === null && series === null && keys.length > 0;
  const hint = anchor !== null || loading ? null : "No history yet";
  const retryable = hint !== null && keys.length > 0;

  const open = (anchor: (typeof anchors)[number]) => {
    const quoteOf = (row: RailBookRow) => ({
      oddsAmerican: decimalToAmerican(row.decimal),
      betLinks: toBetLinkSet(betHref(row, false), row.appLink, undefined) ?? undefined,
      capacity: capacityOf(row, bookStake),
      isBestPrice: row.best ? (true as const) : undefined,
    });
    const lineAt = (value: number | null, side: string) =>
      spec.line === "none" || value === null ? null : spec.line === "spread" && side === "away" ? -value : value;
    const lineOf = (side: string) => lineAt(model.lineValue, side);
    const ladder = model.summaries.length === 0 ? [model] : model.summaries.flatMap((summary) => (summary.line === model.lineValue ? [model] : modelAt(summary.line) ?? []));
    const otherOutcome = model.columns.length === 2 ? model.columns.find((column) => column.outcome !== target.outcome)?.outcome : undefined;
    const rowsOf = (rail: RailModel, outcome: number | undefined) => rail.columns.find((column) => column.outcome === outcome)?.rows ?? [];
    const peersOf = (outcome: number | undefined, own: RailBookRow | undefined): OddsHistoryPeerBook[] =>
      ladder.flatMap((rail) =>
        rowsOf(rail, outcome)
          .filter((row) => row.selectionId !== null && !(rail === model && row.book === own?.book))
          .map((row) => ({ book: row.book, bookDisplayName: getBookDisplayName(row.book), selectionId: row.selectionId ?? undefined, side: row.side ?? undefined, line: lineAt(rail.lineValue, row.side ?? anchor.side), ...quoteOf(row) })),
      );
    const otherAnchor = rowsOf(model, otherOutcome).find((row) => row.book === anchor.row.book);
    const otherSide = otherOutcome === undefined ? null : otherAnchor?.side ?? spec.outcomes[otherOutcome] ?? null;
    const main = model.summaries.find((summary) => summary.isMain)?.line ?? null;
    const selection: OddsHistorySelection = {
      eventId: target.event,
      book: anchor.row.book,
      selectionId: anchor.row.selectionId ?? undefined,
      side: anchor.side,
      line: lineOf(anchor.side),
      mainLine: lineAt(main, anchor.side),
      market: spec.name,
      marketLabel: spec.label,
      openerMarket: target.family === "game_lines" && spec.period === "full" ? { id: spec.id, lined: spec.line !== "none" } : undefined,
      entity: entityName,
      eventStart,
      matchup: matchupLabel(event, target.league),
      teams: event,
      opposite:
        otherSide === null
          ? undefined
          : { selectionId: otherAnchor?.selectionId ?? undefined, side: otherSide, line: lineOf(otherSide), context: otherAnchor ? { quote: quoteOf(otherAnchor) } : undefined, peers: peersOf(otherOutcome, otherAnchor) },
      context: { quote: quoteOf(anchor.row) },
    };
    if (!canAnchorOddsHistory(selection)) return;
    onOpenHistory({ selection, fallbackSeries: anchor.series, peers: peersOf(target.outcome, anchor.row) });
    phCapture("odds_screen_line_history_opened", { league: target.league, family: target.family, market: spec.id, book: anchor.row.book });
  };

  useEffect(() => {
    if (openFor === undefined || series === null) return;
    const requested = anchors.find((candidate) => candidate.row.book === openFor.book);
    if (requested !== undefined) open(requested);
    openFor.onOpened();
  }, [series, openFor]);

  return (
    <div className="flex items-center border-b border-surface-line">
      <button
        type="button"
        onClick={() => anchor !== null && open(anchor)}
        disabled={anchor === null}
        aria-haspopup="dialog"
        className={`group/history flex ${isMobile ? "min-h-11" : "min-h-10"} min-w-0 flex-grow items-center gap-3 px-4 text-left transition-colors hover:bg-white/[0.04] active:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <ChartSpline className="size-4 flex-shrink-0 text-primary" aria-hidden="true" />
        <span className="flex min-w-0 flex-grow items-baseline gap-2">
          <span className="text-[13px] font-semibold text-foreground">Line History</span>
          {hint !== null && <span className="truncate text-[11px] text-zinc-500">{hint}</span>}
        </span>
        <ChevronRight className="size-4 flex-shrink-0 text-zinc-500 transition-colors group-hover/history:text-foreground" aria-hidden="true" />
      </button>
      {retryable && (
        <button type="button" onClick={onRetry} className={`${isMobile ? "min-h-11" : "min-h-10"} flex-shrink-0 px-4 text-[12px] font-medium text-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40`}>
          Retry
        </button>
      )}
    </div>
  );
}
